import { WizardStep } from '@sec-loans-ui/ifp-loans/wizard/wizardMachine'
import { assertValue } from '@shared/helpers'
import { CommonAfterCareStep } from '@ui-common/components/CommonAfterCareStep'
import { getFullname } from '@ui-common/utils'
import { PaperContainer } from '@ui-components/containers/PaperContainer'
import { Divider } from '@ui-components/decorative/Divider'
import { LogoIconType } from '@ui-components/global/ComponentProps'
import { ListSection } from '@ui-components/global/LayoutStyling'
import { LargeScreenContainer } from '@ui-components/layout/LargeScreenContainer'
import { QuaternaryHeading } from '@ui-components/typography/headings/QuaternaryHeading'
import { SecondaryHeading } from '@ui-components/typography/headings/SecondaryHeading'
import { BodyText } from '@ui-components/typography/Typography'
import { useIntl } from 'react-intl'

import { useIfpAnalyticsService, useIfpPageViewEvent } from '../../analytics/analyticsContext'
import { useSubHeaderOptionsWithMenu } from '../../hooks/useSubHeaderOptionsWithMenu'
import { ApplicantInfoSummary } from '../applicant-info/ApplicantInfoSummary'
import { useIfpFormContext } from '../formContext'
import { HouseholdInfoSummary } from '../household-info/HouseholdInfoSummary'
import { IfpInfoSummary } from '../ifp-info/IfpInfoSummary'
import { validateEndStepState } from './endstepUtils'
import { getIfpAfterCareTranslations } from './translations'

export const EndStep = ({
  onGoToApplicationsClick,
  onNewApplicationClick,
}: {
  onGoToApplicationsClick: () => void
  onNewApplicationClick: () => void
}) => {
  useIfpPageViewEvent(WizardStep.EndStep)
  const { formatMessage } = useIntl()
  const { analyticsService } = useIfpAnalyticsService()
  const { applicationState, shouldShowSentModal, exit, user, loan } = useIfpFormContext()

  validateEndStepState(applicationState)

  const subHeaderOptions = useSubHeaderOptionsWithMenu({
    onGoToApplicationsClick,
    onNewApplicationClick,
  })

  const state = assertValue(applicationState, 'applicationState')

  const afterCareTexts = getIfpAfterCareTranslations({ formatMessage, numDebtors: loan.debtors.length, state })

  const modalProps = shouldShowSentModal
    ? {
        children: <BodyText>{formatMessage({ id: 'sec-loans-the-end-step-modal-description' })}</BodyText>,
      }
    : null

  return (
    <CommonAfterCareStep
      stepId={WizardStep.EndStep}
      icon={LogoIconType.SPankki}
      topic={formatMessage({ id: 'common-read-carefully' })}
      title={formatMessage({ id: 'ifp-application-top-bar-heading' })}
      secondaryHeadingHeader={afterCareTexts.header}
      text={afterCareTexts.content}
      subHeaderOptions={subHeaderOptions}
      headerButtonProps={{
        headerButtonLabel: formatMessage({ id: 'common-exit' }),
        onHeaderButtonClick: exit,
      }}
      headerAdditionalText={getFullname(user)}
      contentAreaContent={<AfterCareSummary />}
      analyticsService={analyticsService}
      sentModalProps={modalProps}
    >
      {afterCareTexts.details && (
        <>
          <QuaternaryHeading header={afterCareTexts.details.header} weight="medium" />
          <ListSection>
            <BodyText>{afterCareTexts.details.content}</BodyText>
          </ListSection>
        </>
      )}
    </CommonAfterCareStep>
  )
}

const AfterCareSummary = () => {
  const { formatMessage } = useIntl()
  const { user, wizardData } = useIfpFormContext()

  const relevantApplicantInfo = wizardData?.applicantInfos?.find((a) => a.uuid === user.debtorUuid)

  return (
    <PaperContainer $largeScreenSupport={true}>
      <LargeScreenContainer>
        <SecondaryHeading header={formatMessage({ id: 'common-status-summary-header' })} />
        <IfpInfoSummary data={assertValue(wizardData?.basicInfo, 'wizardData.basicInfo')} />
        <Divider />
        {wizardData?.householdInfo && (
          <>
            <HouseholdInfoSummary data={wizardData.householdInfo} /> <Divider />
          </>
        )}
        <ApplicantInfoSummary
          index={0}
          data={assertValue(relevantApplicantInfo, 'relevantApplicantInfo')}
          numberOfApplicants={1}
        />
      </LargeScreenContainer>
    </PaperContainer>
  )
}
